import React from "react";
import {
    Box,
    Button,
    Divider,
    Grid,
    IconButton,
    LinearProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Typography,
    useTheme,
} from "@mui/material";
import * as departmentService from "services/department-service";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { department } from "services/department-service";
import DepartmentEditModal from "../modals/departmentEdit.modal";
import DepartmentCreateModal from "../modals/departmentCreate.modal";
import { useTranslation } from "react-i18next";
import * as Styles from "pages/development/settings/Settings.styles";
import { IPropsSettings } from "../Settings";
import DepartmentDeleteModal from "../modals/departmentDelete.modal";

const DepartmentsTab = (props: IPropsSettings) => {
    const { tenantId } = props;
    const theme = useTheme();
    const { t } = useTranslation();
    const [departments, setDepartments] = React.useState<department[]>([]);
    const [openDepartment, setOpenDepartment] = React.useState<
        department | undefined
    >(undefined);
    const [openCreate, setOpenCreate] = React.useState<boolean>(false);
    const [openDelete, setOpenDelete] = React.useState<boolean>(false);
    const [loading, setLoading] = React.useState<boolean>(false);

    const getDepartments = (): void => {
        departmentService.getDepartments(tenantId).then((res) => {
            setDepartments(res);
            setLoading(false);
        });
    };

    React.useEffect(() => {
        setLoading(true);
        getDepartments();
    }, [tenantId]);

    const handleClose = (updatedDepartment?: department) => {
        if (openCreate) {
            if (updatedDepartment !== undefined) {
                setDepartments([...departments, updatedDepartment]);
            }
            setOpenCreate(false);
            return;
        }
        if (updatedDepartment !== undefined) {
            setDepartments(
                departments.map((department) => {
                    if (department.id === updatedDepartment.id) {
                        return updatedDepartment;
                    }
                    return department;
                }),
            );
        }
        setOpenDepartment(undefined);
    };

    const handleDeleteClose = (): void => {
        setOpenDepartment(undefined);
        setOpenDelete(false);
        setLoading(true);
        getDepartments();
    };

    return (
        <Box>
            <Typography variant="h4">
                {t("settings.departments.title")}
            </Typography>
            <Box>{t("settings.departments.description")}</Box>
            {loading && (
                <Box sx={{ marginTop: "1rem" }}>
                    <LinearProgress />
                </Box>
            )}
            {!loading && (
                <React.Fragment>
                    <Box sx={{ marginTop: "1rem" }}>
                        <Grid
                            container
                            spacing={2}
                            sx={Styles.infoWrapper(theme)}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    width: "100%",
                                    marginTop: "1rem",
                                }}
                            >
                                <Button
                                    variant="contained"
                                    onClick={() => setOpenCreate(true)}
                                >
                                    {t("departments.new")}
                                </Button>
                            </Box>
                            <Divider
                                sx={{ width: "100%", marginTop: "1rem" }}
                            />
                            <TableContainer>
                                <Table
                                    sx={{ minWidth: 700 }}
                                    aria-label="customized table"
                                    size="small"
                                >
                                    <TableBody>
                                        {departments.map((department) => (
                                            <TableRow
                                                hover
                                                key={department.id}
                                                sx={{
                                                    backgroundColor: "white",
                                                }}
                                            >
                                                <TableCell
                                                    component="th"
                                                    scope="row"
                                                >
                                                    {department.name}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <IconButton
                                                        onClick={() =>
                                                            setOpenDepartment(
                                                                department,
                                                            )
                                                        }
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                    <IconButton
                                                        onClick={() => {
                                                            setOpenDepartment(
                                                                department,
                                                            );
                                                            setOpenDelete(true);
                                                        }}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Grid>
                    </Box>
                    {openDepartment !== undefined && (
                        <DepartmentEditModal
                            open={openDepartment !== undefined}
                            onClose={handleClose}
                            department={openDepartment}
                            tenantId={tenantId}
                        />
                    )}
                    {openDepartment !== undefined && (
                        <DepartmentDeleteModal
                            open={openDelete}
                            onClose={handleDeleteClose}
                            department={openDepartment}
                            tenantId={tenantId}
                        />
                    )}
                    <DepartmentCreateModal
                        open={openCreate}
                        onClose={handleClose}
                        tenantId={tenantId}
                    />
                </React.Fragment>
            )}
        </Box>
    );
};

export default DepartmentsTab;
